import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";

const PUB = process.env.PUBLIC_URL || "";

const GROUPS = [
  {
    key: "commune",
    label: "Notre commune",
    items: [
      { to: "/commune", label: "Présentation" },
      { to: "/rubrique/histoire", label: "Histoire" },
    ],
  },
  {
    key: "quotidien",
    label: "Vie quotidienne",
    items: [
      { to: "/demarches", label: "Démarches" },
      { to: "/signalements", label: "Signalements" },
      { to: "/cartographie", label: "Cartographie" },
      { to: "/calendrier", label: "Calendrier" },
    ],
  },
  {
    key: "locale",
    label: "Vie locale",
    items: [
      { to: "/actualites", label: "Actualités" },
      { to: "/vie-locale", label: "Associations & commerces" },
      { to: "/rubrique/ecole", label: "École" },
      { to: "/rubrique/salle-des-fetes", label: "Salle communale" },
      { to: "/bibliotheque", label: "Bibliothèque" },
      { to: "/bar-associatif", label: "Bar associatif" },
      { to: "/circuits-randonnee", label: "Circuits de randonnée" },
    ],
  },
];

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);  // mobile
  const [clickOpen, setClickOpen] = useState(null); // clé du groupe ouvert au clic
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const navRef = useRef(null);
  const [cmNew, setCmNew] = useState(false);

  // Badge "nouveau PV de conseil municipal" : compare la derniere date de
  // publication a celle deja vue par ce navigateur (localStorage).
  useEffect(() => {
    let cancelled = false;
    api.get("/conseils/latest").then((r) => {
      if (cancelled || !r || !r.latest) return;
      let seen = "";
      try { seen = localStorage.getItem("mairie_cm_last_seen") || ""; } catch { /* ignore */ }
      if (r.latest.date_publication > seen) setCmNew(true);
    }).catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, []);

  const closeAll = () => { setMenuOpen(false); setClickOpen(null); };
  const doLogout = async () => { await logout(); closeAll(); nav("/"); };

  // Fermer le sous-menu si clic en dehors de la nav
  useEffect(() => {
    if (!clickOpen) return;
    const h = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setClickOpen(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [clickOpen]);

  // Fermer avec Escape
  useEffect(() => {
    if (!clickOpen) return;
    const h = (e) => { if (e.key === "Escape") setClickOpen(null); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [clickOpen]);

  return (
    <nav className="nav-v2" ref={navRef}>
      <div className="nav-v2-inner">
        <Link to="/" className="nav-v2-brand" onClick={closeAll}>
          <span className="nav-v2-crest">
            <img src={`${PUB}/logo-lcsf.png`} alt="Blason" />
          </span>
        </Link>

        <button className="nav-v2-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
          {menuOpen ? "\u2715" : "\u2630"}
        </button>

        <div className={`nav-v2-links ${menuOpen ? "open" : ""}`}>
          <NavLink to="/" end onClick={closeAll} className="nav-v2-link">Accueil</NavLink>

          {GROUPS.map((g) => {
            const isOpen = clickOpen === g.key;
            return (
            <React.Fragment key={g.key}>
              <div className={`nav-v2-group ${isOpen ? "active" : ""}`}>
                <button
                  type="button"
                  className="nav-v2-link nav-v2-grouptrigger"
                  aria-expanded={isOpen}
                  onClick={() => setClickOpen(isOpen ? null : g.key)}
                >
                  {g.label}
                  <span className="nav-v2-caret">▾</span>
                </button>
                {/* Le sous-menu est TOUJOURS rendu (visible via CSS au :hover du parent ou si actif) */}
                <div className="nav-v2-submenu">
                  {g.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      onClick={closeAll}
                      className="nav-v2-subitem"
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              {/* [lot4] Conseil municipal : lien autonome entre "Notre commune" et "Vie quotidienne" */}
              {g.key === "commune" && (
                <NavLink to="/conseil-municipal" onClick={closeAll} className="nav-v2-link nav-v2-conseil">
                  Conseil municipal
                  {cmNew && (
                    <span className="nav-v2-badge" title="Nouveau compte rendu de conseil municipal">1</span>
                  )}
                </NavLink>
              )}
            </React.Fragment>
            );
          })}


          {user ? (
            <>
              {user.is_admin && (
                <NavLink to="/admin" onClick={closeAll} className="nav-v2-link">Admin</NavLink>
              )}
              {user.is_libraire && !user.is_admin && (
                <NavLink to="/espace-libraire" onClick={closeAll} className="nav-v2-link">
                  📚 Espace libraire
                </NavLink>
              )}
              {user.is_barman && !user.is_admin && (
                <NavLink to="/espace-barman" onClick={closeAll} className="nav-v2-link">
                  🍻 Espace barman
                </NavLink>
              )}
              <button className="nav-v2-logout" onClick={doLogout}>Déconnexion</button>
            </>
          ) : (
            <NavLink to="/connexion" onClick={closeAll} className="nav-v2-connexion">
              Connexion
            </NavLink>
          )}
        </div>
      </div>
    </nav>
  );
}
